import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Plus, Search, UserPlus, Trash2, Eye, Calendar, MapPin, Users, Clock, CheckCircle, XCircle, AlertCircle, QrCode, Download, ExternalLink, Phone, Mail, User, History, DollarSign, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import ActivityHistoryModal from '../components/ActivityHistoryModal';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEMBERSHIP_STATUSES = [
  { value: 'Registered', label: 'Registered' },
  { value: 'RSVP Yes', label: 'RSVP Yes' },
  { value: 'RSVP No', label: 'RSVP No' },
  { value: 'Attended', label: 'Attended' },
  { value: 'No Show', label: 'No Show' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'Registered': return 'bg-slate-100 text-slate-800';
    case 'RSVP Yes': return 'bg-green-100 text-green-800';
    case 'RSVP No': return 'bg-red-100 text-red-800';
    case 'Attended': return 'bg-emerald-100 text-emerald-800';
    case 'No Show': return 'bg-orange-100 text-orange-800';
    case 'Cancelled': return 'bg-gray-100 text-gray-500';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getCategoryBadgeColor = (category) => {
  switch (category) {
    case 'Office Event': return 'bg-purple-100 text-purple-800';
    case 'Sales Week': return 'bg-green-100 text-green-800';
    case 'Outside Event': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Stage labels for lead detail view
const STAGE_LABELS = {
  cold_data: 'Cold Data',
  attempting_contact: 'Attempting Contact',
  contacted: 'Contacted',
  qualified: 'Qualified',
  consultation_scheduled: 'Consultation Scheduled',
  consultation_completed: 'Consultation Completed',
  warm_follow_up: 'Warm Follow-Up',
  rsvp_office_event: 'RSVP Office Event',
  attended_office_event: 'Attended Office Event',
  long_term_nurture: 'Long-Term Nurture',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  disqualified: 'Disqualified'
};

const stageColors = {
  cold_data: 'bg-blue-100 text-blue-800',
  attempting_contact: 'bg-yellow-100 text-yellow-800',
  contacted: 'bg-orange-100 text-orange-800',
  qualified: 'bg-purple-100 text-purple-800',
  consultation_scheduled: 'bg-indigo-100 text-indigo-800',
  consultation_completed: 'bg-teal-100 text-teal-800',
  warm_follow_up: 'bg-pink-100 text-pink-800',
  rsvp_office_event: 'bg-cyan-100 text-cyan-800',
  attended_office_event: 'bg-emerald-100 text-emerald-800',
  long_term_nurture: 'bg-gray-100 text-gray-800',
  closed_won: 'bg-green-100 text-green-800',
  closed_lost: 'bg-red-100 text-red-800',
  disqualified: 'bg-slate-100 text-slate-500'
};

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsInEvent, setLeadsInEvent] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  
  // Lead detail view state
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLeadViewOpen, setIsLeadViewOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [activityLeadId, setActivityLeadId] = useState(null);
  const [activityLeadName, setActivityLeadName] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [addMembershipNotes, setAddMembershipNotes] = useState('');
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrMembership, setQrMembership] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  
  const { hasPermission, role, user } = usePermissions();
  const canAddOwn = hasPermission('event_members_add_own');
  const canAddAny = hasPermission('event_members_add_any');
  const canUpdateOwn = hasPermission('event_members_update_own');
  const canUpdateAny = hasPermission('event_members_update_any');
  const canRemoveAny = hasPermission('event_members_remove_any');
  const isReadOnly = role === 'top_management' || role === 'finance';

  useEffect(() => {
    fetchEventDetails();
    fetchMemberships();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (canAddOwn || canAddAny) {
      fetchLeads();
    }
  }, [canAddOwn, canAddAny]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEventDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
      } else {
        toast.error('Event not found');
        navigate('/events');
      }
    } catch (error) {
      toast.error('Failed to fetch event details');
    }
  };

  const fetchMemberships = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/memberships`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMemberships(data);
        setLeadsInEvent(new Set(data.map(m => m.lead_id)));
      }
    } catch (error) {
      toast.error('Failed to fetch memberships');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      logger.error('Failed to fetch leads:', error);
    }
  };

  const handleAddLead = async () => {
    if (!selectedLeadId) {
      toast.error('Please select a lead');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/memberships`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lead_id: selectedLeadId,
          status: 'Registered',
          source: 'Sales Team',
          notes: addMembershipNotes || null
        })
      });

      if (response.ok) {
        toast.success('Lead added to event');
        setIsAddLeadOpen(false);
        setSelectedLeadId('');
        setAddMembershipNotes('');
        fetchMemberships();
        fetchEventDetails(); // Refresh counts
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to add lead');
      }
    } catch (error) {
      toast.error('Failed to add lead to event');
    }
  };

  const handleStatusChange = async (membershipId, newStatus, membership) => {
    // Check permissions
    if (role === 'account_executive') {
      if (membership.lead_owner !== user?.user_id) {
        toast.error('You can only update memberships for your own leads');
        return;
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/event-memberships/${membershipId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        fetchMemberships();
        fetchEventDetails(); // Refresh counts
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRemoveMembership = async (membershipId) => {
    if (!window.confirm('Remove this lead from the event?')) return;

    try {
      const response = await fetch(`${API_URL}/api/event-memberships/${membershipId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Membership removed');
        fetchMemberships();
        fetchEventDetails();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove membership');
      }
    } catch (error) {
      toast.error('Failed to remove membership');
    }
  };

  // Fetch and show lead details
  const handleViewLead = async (leadId, leadName) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const lead = await response.json();
        setSelectedLead(lead);
        setIsLeadViewOpen(true);
      } else {
        toast.error('Failed to load lead details');
      }
    } catch (error) {
      logger.error('Error fetching lead:', error);
      toast.error('Failed to load lead details');
    }
  };

  // Show activity history modal
  const handleShowActivity = (leadId, leadName) => {
    setActivityLeadId(leadId);
    setActivityLeadName(leadName);
    setIsActivityModalOpen(true);
  };

  const handleShowQR = async (membership) => {
    setQrMembership(membership);
    setQrModalOpen(true);
    
    try {
      const response = await fetch(`${API_URL}/api/event-memberships/${membership.membership_id}/qr`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setQrImageUrl(url);
      } else {
        toast.error('Failed to generate QR code');
      }
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const handleDownloadQR = () => {
    if (qrImageUrl && qrMembership) {
      const link = document.createElement('a');
      link.href = qrImageUrl;
      link.download = `checkin-${qrMembership.lead_name?.replace(/\s+/g, '-') || 'guest'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const closeQrModal = () => {
    setQrModalOpen(false);
    if (qrImageUrl) {
      URL.revokeObjectURL(qrImageUrl);
      setQrImageUrl(null);
    }
    setQrMembership(null);
  };

  const formatDisplayDate = (isoString) => {
    try {
      return format(new Date(isoString), 'EEEE, MMMM d, yyyy • h:mm a');
    } catch {
      return isoString;
    }
  };

  const canEditMembership = (membership) => {
    // Read-only roles cannot edit
    if (isReadOnly) return false;
    // Anyone with update permission can edit any membership (including AEs for attendance)
    if (canUpdateAny || canUpdateOwn) return true;
    // Fallback: Owner/Admin/Sales Manager by role
    if (['owner', 'admin', 'sales_manager', 'account_executive'].includes(role)) return true;
    return false;
  };

  // Get unique owners from memberships for the filter dropdown
  const uniqueOwners = [...new Map(
    memberships
      .filter(m => m.lead_owner && m.owner_name)
      .map(m => [m.lead_owner, { id: m.lead_owner, name: m.owner_name }])
  ).values()].sort((a, b) => a.name.localeCompare(b.name));

  // Filter memberships based on search, status, and owner
  const filteredMemberships = memberships.filter(m => {
    const matchesSearch = !searchQuery || 
      m.lead_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.lead_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.lead_phone?.includes(searchQuery);
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    const matchesOwner = ownerFilter === 'all' || m.lead_owner === ownerFilter;
    return matchesSearch && matchesStatus && matchesOwner;
  });

  // Filter leads for add dialog (exclude already added)
  const availableLeads = leads.filter(lead => {
    if (leadsInEvent.has(lead.lead_id)) return false;
    if (!leadSearchQuery) return true;
    const searchLower = leadSearchQuery.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(searchLower) ||
      lead.last_name?.toLowerCase().includes(searchLower) ||
      lead.email?.toLowerCase().includes(searchLower) ||
      lead.phone?.includes(leadSearchQuery)
    );
  }).slice(0, 50);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Event not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="event-detail-page">
      {/* Back Button & Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/events')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>
      </div>

      {/* Event Info Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <CardTitle className="text-2xl font-bold">{event.name}</CardTitle>
                <Badge className={getCategoryBadgeColor(event.category)}>{event.category}</Badge>
                {event.is_active ? (
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDisplayDate(event.start_datetime)}
                </div>
                {event.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </div>
                )}
                {event.capacity && (
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Capacity: {event.capacity}
                  </div>
                )}
              </div>
            </div>
            {(canAddOwn || canAddAny) && !isReadOnly && (
              <Button 
                onClick={() => setIsAddLeadOpen(true)}
                className="bg-gold hover:bg-gold/90 text-slate-900"
                data-testid="add-lead-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{event.total_members || 0}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{event.registrations || 0}</p>
              <p className="text-sm text-blue-600">Registered</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{event.rsvp_yes || 0}</p>
              <p className="text-sm text-green-600">RSVP Yes</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{event.attended || 0}</p>
              <p className="text-sm text-emerald-600">Attended</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-orange-700">{event.no_shows || 0}</p>
              <p className="text-sm text-orange-600">No Shows</p>
            </div>
          </div>

          {event.description && (
            <p className="text-slate-600 text-sm">{event.description}</p>
          )}
        </CardContent>
      </Card>

      {/* Memberships Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">Event Members ({filteredMemberships.length})</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                  data-testid="search-members"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {MEMBERSHIP_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="filter-owner">
                  <SelectValue placeholder="All Owners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Owners</SelectItem>
                  {uniqueOwners.map(owner => (
                    <SelectItem key={owner.id} value={owner.id}>{owner.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredMemberships.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No members found</p>
              {(canAddOwn || canAddAny) && !isReadOnly && (
                <Button 
                  variant="link" 
                  onClick={() => setIsAddLeadOpen(true)}
                  className="text-gold mt-2"
                >
                  Add your first member
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead className="text-center">Check-in</TableHead>
                    {!isReadOnly && <TableHead></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMemberships.map((membership) => (
                    <TableRow key={membership.membership_id} data-testid={`membership-row-${membership.membership_id}`}>
                      <TableCell>
                        <button
                          onClick={() => handleViewLead(membership.lead_id, membership.lead_name)}
                          className="font-medium text-slate-900 hover:text-gold hover:underline text-left"
                          data-testid={`view-lead-${membership.lead_id}`}
                        >
                          {membership.lead_name || 'Unknown'}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        <div>{membership.lead_email}</div>
                        <div>{membership.lead_phone}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {membership.lead_stage?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {membership.owner_name || 'Unassigned'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {membership.source}
                      </TableCell>
                      <TableCell>
                        {canEditMembership(membership) ? (
                          <Select
                            value={membership.status}
                            onValueChange={(value) => handleStatusChange(membership.membership_id, value, membership)}
                          >
                            <SelectTrigger className="w-32 h-8" data-testid={`status-select-${membership.membership_id}`}>
                              <Badge className={getStatusBadgeColor(membership.status)}>
                                {membership.status}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {MEMBERSHIP_STATUSES.map(s => (
                                <SelectItem key={s.value} value={s.value}>
                                  <Badge className={getStatusBadgeColor(s.value)}>{s.label}</Badge>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={getStatusBadgeColor(membership.status)}>
                            {membership.status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {membership.created_at && format(new Date(membership.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleShowQR(membership)}
                          className="text-slate-500 hover:text-gold hover:bg-gold/10"
                          title="Show QR Code for Check-in"
                          data-testid={`qr-btn-${membership.membership_id}`}
                        >
                          <QrCode className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell>
                          {canRemoveAny && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveMembership(membership.membership_id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-btn-${membership.membership_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Lead Dialog */}
      <Dialog open={isAddLeadOpen} onOpenChange={setIsAddLeadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Lead to Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Search Leads</Label>
              <Input
                placeholder="Search by name, email, or phone..."
                value={leadSearchQuery}
                onChange={(e) => setLeadSearchQuery(e.target.value)}
                data-testid="search-leads-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Select Lead</Label>
              <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                <SelectTrigger data-testid="select-lead">
                  <SelectValue placeholder="Choose a lead..." />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {availableLeads.length === 0 ? (
                    <div className="p-2 text-sm text-slate-500 text-center">
                      {leadSearchQuery ? 'No matching leads found' : 'No available leads'}
                    </div>
                  ) : (
                    availableLeads.map(lead => (
                      <SelectItem key={lead.lead_id} value={lead.lead_id}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">
                            {lead.first_name} {lead.last_name}
                          </span>
                          <span className="text-xs text-slate-500">
                            {lead.email || lead.phone}
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Add any notes about this registration..."
                value={addMembershipNotes}
                onChange={(e) => setAddMembershipNotes(e.target.value)}
                className="min-h-[80px]"
                data-testid="membership-notes"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setIsAddLeadOpen(false); setSelectedLeadId(''); setLeadSearchQuery(''); setAddMembershipNotes(''); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddLead}
                disabled={!selectedLeadId}
                className="bg-gold hover:bg-gold/90 text-slate-900"
                data-testid="confirm-add-lead-btn"
              >
                Add to Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={qrModalOpen} onOpenChange={closeQrModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">Event Check-in QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {qrMembership && (
              <div className="text-center mb-4">
                <p className="font-semibold text-lg">{qrMembership.lead_name}</p>
                <p className="text-sm text-slate-500">{event?.name}</p>
              </div>
            )}
            
            {qrImageUrl ? (
              <div className="bg-white p-4 rounded-lg shadow-inner border">
                <img 
                  src={qrImageUrl} 
                  alt="Check-in QR Code" 
                  className="w-64 h-64"
                  data-testid="qr-code-image"
                />
              </div>
            ) : (
              <div className="w-64 h-64 bg-slate-100 rounded-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
              </div>
            )}
            
            <p className="text-xs text-slate-500 text-center mt-4 mb-4">
              Scan this QR code at the event entrance to mark attendance instantly
            </p>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadQR}
                disabled={!qrImageUrl}
                className="flex items-center gap-2"
                data-testid="download-qr-btn"
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
              <Button
                onClick={closeQrModal}
                className="bg-gold hover:bg-gold/90 text-slate-900"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Sheet */}
      <Sheet open={isLeadViewOpen} onOpenChange={setIsLeadViewOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading text-xl">Lead Details</SheetTitle>
          </SheetHeader>
          {selectedLead && (
            <div className="mt-6 space-y-6">
              {/* Lead Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12 bg-gold/20">
                    <AvatarFallback className="text-gold font-medium">
                      {selectedLead.first_name?.[0]}{selectedLead.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg">
                      {selectedLead.first_name} {selectedLead.last_name}
                    </h3>
                    <Badge className={stageColors[selectedLead.stage] || 'bg-gray-100'}>
                      {STAGE_LABELS[selectedLead.stage] || selectedLead.stage}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{selectedLead.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{selectedLead.email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-slate-400" />
                    <span>{selectedLead.lead_source || selectedLead.source || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    <span>{selectedLead.product_interest || '-'}</span>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-500 mb-1">Created</p>
                  <p className="text-sm">{selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleDateString() : '-'}</p>
                </div>
                
                {selectedLead.notes && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Notes</p>
                    <p className="text-sm bg-slate-50 p-2 rounded">{selectedLead.notes}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleShowActivity(selectedLead.lead_id, `${selectedLead.first_name} ${selectedLead.last_name}`);
                  }}
                  className="flex-1"
                >
                  <History className="w-4 h-4 mr-2" />
                  Activity History
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/leads?search=${encodeURIComponent(selectedLead.phone || selectedLead.email || '')}`)}
                  className="flex-1"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Go to Lead
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Activity History Modal */}
      <ActivityHistoryModal
        isOpen={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
        leadId={activityLeadId}
        leadName={activityLeadName}
      />
    </div>
  );
}
